import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { templates } from '../data/templates';

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<'General' | 'Trending'>('General');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  // Get all unique tags from templates (excluding General and Trending)
  const allTags = Array.from(
    new Set(templates.flatMap((t) => t.tags || []))
  ).filter((tag) => tag !== 'General' && tag !== 'Trending');

  // Filter templates based on category and tag
  const filteredTemplates = templates.filter((template) => {
    // Category filter
    if (selectedCategory === 'General' && !template.tags?.includes('General')) {
      return false;
    }
    if (selectedCategory === 'Trending' && !template.tags?.includes('Trending')) {
      return false;
    }

    // Tag filter
    if (selectedTag && !template.tags?.includes(selectedTag)) {
      return false;
    }

    return true;
  });

  const handleTemplateClick = (templateId: string) => {
    navigate(`/template-to-video?templateId=${templateId}`);
  };

  return (
    <div className="min-h-full bg-bg py-12">
      <div className="fluid-container">
        {/* Controls Row */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8 flex-wrap">
          {/* Search and Bookmark Icons */}
          <div className="flex gap-2">
            <button className="w-10 h-10 bg-black/50 border border-white/20 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors">
              <svg
                className="w-5 h-5 text-white/70"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </button>
            <button className="w-10 h-10 bg-black/50 border border-white/20 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors">
              <svg
                className="w-5 h-5 text-white/70"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                />
              </svg>
            </button>
          </div>

          {/* Category Pills */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setSelectedCategory('General')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === 'General'
                  ? 'bg-white text-black'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              General
            </button>
            <button
              onClick={() => setSelectedCategory('Trending')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === 'Trending'
                  ? 'bg-white text-black'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              Trending
            </button>
          </div>

          {/* Tag Chips - Horizontal Scrollable */}
          <div className="flex gap-2 overflow-x-auto flex-1 min-w-0 pb-2 -mb-2 scrollbar-hide">
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  selectedTag === tag
                    ? 'bg-white text-black'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                {tag.toLowerCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Gallery Grid - Fluid Layout */}
        <div className="auto-grid">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              onClick={() => handleTemplateClick(template.id)}
              className="group relative break-inside-avoid mb-4 rounded-lg overflow-hidden border border-white/20 bg-white/5 cursor-pointer hover:scale-105 hover:brightness-110 transition-all duration-300"
            >
              {/* Background Image */}
              <div className="relative aspect-[9/16]">
                <img
                  src={template.thumbnailUrl}
                  alt={template.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to gradient placeholder
                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="711"%3E%3Cdefs%3E%3ClinearGradient id="grad" x1="0%25" y1="0%25" x2="100%25" y2="100%25"%3E%3Cstop offset="0%25" style="stop-color:%23333;stop-opacity:1" /%3E%3Cstop offset="100%25" style="stop-color:%23555;stop-opacity:1" /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill="url(%23grad)" width="400" height="711"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="20" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ETemplate%3C/text%3E%3C/svg%3E';
                  }}
                />

                {/* Rating Badge - Top Right */}
                {template.rating && (
                  <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
                    <span className="text-white text-xs font-medium">{template.rating}</span>
                    <svg
                      className="w-3 h-3 text-yellow-400 fill-current"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                )}

                {/* Overlay at Bottom */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-4">
                  <h3 className="text-white font-bold text-lg mb-1">{template.name}</h3>
                  {template.creator && (
                    <p className="text-white/70 text-sm mb-2">@{template.creator}</p>
                  )}
                  {template.badge && (
                    <span
                      className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                        template.badge === 'New'
                          ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                          : 'bg-gradient-to-r from-pink-500 to-orange-500 text-white'
                      }`}
                    >
                      {template.badge}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredTemplates.length === 0 && (
          <div className="text-center py-12 text-white/60">
            No templates found matching your filters.
          </div>
        )}
      </div>
    </div>
  );
}

